import React, { useEffect, useState } from 'react';
import { Row, Col, Form, Spinner, Modal, Alert } from 'react-bootstrap';
import {
  FaBuilding,
  FaMicrochip
} from 'react-icons/fa';
import ApiGServer from '../../../services/ApiGServer';
import ApiMServer from '../../../services/ApiMServer';
import { ReactMultiEmail, isEmail } from 'react-multi-email';
import 'react-multi-email/dist/style.css';
import './NotificationForm.css';
import { NotifyModal } from '../../../components/NotifyModal/NotifyModal';
import DashMedidasSectionCard from '../../DashMedidas/components/DashMedidasSectionCard/DashMedidasSectionCard';
import DashMedidasButton from '../../DashMedidas/components/DashMedidasButton/DashMedidasButton';

const verdeEscuro = "#1c4220";

// Configuração padrão para equipamentos PRAR (Dryer)
const defaultPRAREquipment = {
  _id: "",
  nameEquipament: "",
  dcal: "",
  mscal: "",
  flagcert: false,
  isentkey: false,
  lat: "",
  lng: "",
  daystoactivate: "",
  mode: "",
  plan: "",
  serialnumber: "",
  copo: "",
  equipid: "",
  cupid: "",
  versaosw: "",
  versaofwpri: "",
  versaofwosc: "",
  sha1curva: "",
  wf: "",
  calph: "",
  serialnumbersequence: "",
  createdAt: "",
  updatedAt: "",
  __v: 0,
  address: {
    formatedAddr: "",
    city: {
      longname: "",
      shortname: "",
      lat: "",
      lng: ""
    },
    state: {
      longname: "",
      shortname: "",
      lat: "",
      lng: ""
    },
    country: {
      longname: "",
      shortname: "",
      lat: "",
      lng: ""
    }
  },
  notification: {
    channel: {
      email: false,
      app: false,
      sms: false,
      notifyTo: []
    },
    changeHumidity: false,
    changeTemperature: false,
    changePH: false,
    changeDensity: false,
    changeCurve: false,
    status: false,
    minHumidity: 10,
    maxHumidity: 15,
    minTemperature: 10,
    maxTemperature: 50,
    minPH: 50,
    maxPH: 90,
    minDensity: 500,
    maxDensity: 900
  }
};

// Configuração padrão para equipamentos SBAG (Silo Bag)
const defaultSBAGEquipment = {
  _id: "",
  nameEquipament: "",
  serialnumber: "",
  createdAt: "",
  updatedAt: "",
  __v: 0,
  notification: {
    channel: {
      email: false,
      app: false,
      sms: false,
      notifyTo: []
    },
    changeUR: false,
    changeTA: false,
    changeTC: false,
    changeTG: false,
    changeCO2: false,
    changeBattery: false,
    changeSignal: false,
    minUR: 0,
    maxUR: 100,
    minTA: -20,
    maxTA: 60,
    minTC: -20,
    maxTC: 60,
    minTG: -20,
    maxTG: 60,
    minCO2: 0,
    maxCO2: 5000,
    minBattery: 0,
    maxBattery: 100,
    minSignal: 0,
    maxSignal: 100,
    selectedSensors: [] // Array para armazenar sensores selecionados (sensor1, sensor2, etc.)
  }
};

// Função para determinar o tipo de equipamento baseado no serial number
const getEquipmentType = (serialNumber) => {
  if (!serialNumber) return 'UNKNOWN';
  if (serialNumber.startsWith('PRAR')) return 'PRAR';
  if (serialNumber.startsWith('SBAG')) return 'SBAG';
  return 'UNKNOWN';
};

// Função para obter a configuração padrão baseada no tipo de equipamento
const getDefaultEquipment = (equipmentType) => {
  switch (equipmentType) {
    case 'PRAR':
      return defaultPRAREquipment;
    case 'SBAG':
      return defaultSBAGEquipment;
    default:
      return defaultPRAREquipment; // Fallback para PRAR
  }
};

// Manter compatibilidade com código existente


// Funções de transformação para os novos endpoints
const transformToAlertTypesFormat = (notification, equipmentSerial, equipmentName) => {
  return {
    equipmentSerial,
    equipmentName,
    isActive: true,
    notificationMethods: {
      email: notification.channel?.email || false,
      app: notification.channel?.app || false
    },
    recipients: {
      emails: notification.channel?.notifyTo || []
    },
    alertTypes: {
      ur: {
        enabled: notification.changeUR || false,
        minValue: notification.minUR || null,
        maxValue: notification.maxUR || null,
        unit: "%"
      },
      ta: {
        enabled: notification.changeTA || false,
        minValue: notification.minTA || null,
        maxValue: notification.maxTA || null,
        unit: "°C"
      },
      tc: {
        enabled: notification.changeTC || false,
        minValue: notification.minTC || null,
        maxValue: notification.maxTC || null,
        unit: "°C"
      },
      tg: {
        enabled: notification.changeTG || false,
        minValue: notification.minTG || null,
        maxValue: notification.maxTG || null,
        unit: "°C"
      },
      co2: {
        enabled: notification.changeCO2 || false,
        minValue: notification.minCO2 || null,
        maxValue: notification.maxCO2 || null,
        unit: "ppm"
      },
      battery: {
        enabled: notification.changeBattery || false,
        minValue: notification.minBattery || null,
        maxValue: notification.maxBattery || null,
        unit: "%"
      },
      signal: {
        enabled: notification.changeSignal || false,
        minValue: notification.minSignal || null,
        maxValue: notification.maxSignal || null,
        unit: "dBm"
      }
    }
  };
};

const transformToMinimalFormat = (notification, equipmentSerial, selectedSensors) => {
  const individualSensors = selectedSensors.map((sensorId, index) => ({
    sensorId: (index + 1).toString(),
    sensorName: `Sensor ${index + 1}`,
    enabled: true
  }));

  return {
    equipmentSerial,
    isActive: true,
    notificationMethods: {
      email: notification.channel?.email || false,
      app: notification.channel?.app || false
    },
    recipients: {
      emails: notification.channel?.notifyTo || []
    },
    individualSensors
  };
};

const transformToIndividualSensorsFormat = (notification, equipmentSerial, equipmentName, selectedSensors) => {
  const sensorMapping = {
    sensor1: { name: "Sensor 1", unit: "%" },
    sensor2: { name: "Sensor 2", unit: "°C" },
    sensor3: { name: "Sensor 3", unit: "ppm" },
    sensor4: { name: "Sensor 4", unit: "°C" },
    sensor5: { name: "Sensor 5", unit: "°C" }
  };

  const individualSensors = selectedSensors.map((sensorId, index) => {
    const sensorInfo = sensorMapping[sensorId];
    let enabled = false;
    let minValue = null;
    let maxValue = null;
    let unit = sensorInfo?.unit || "";

    // Mapear sensores para os tipos de alerta correspondentes
    switch (sensorId) {
      case 'sensor1':
        enabled = notification.changeUR || false;
        minValue = notification.minUR || null;
        maxValue = notification.maxUR || null;
        break;
      case 'sensor2':
        enabled = notification.changeTA || false;
        minValue = notification.minTA || null;
        maxValue = notification.maxTA || null;
        break;
      case 'sensor3':
        enabled = notification.changeCO2 || false;
        minValue = notification.minCO2 || null;
        maxValue = notification.maxCO2 || null;
        break;
      case 'sensor4':
        enabled = notification.changeTG || false;
        minValue = notification.minTG || null;
        maxValue = notification.maxTG || null;
        break;
      case 'sensor5':
        enabled = notification.changeTC || false;
        minValue = notification.minTC || null;
        maxValue = notification.maxTC || null;
        break;
      default:
        break;
    }

    const sensor = {
      sensorId: (index + 1).toString(),
      sensorName: sensorInfo?.name || `Sensor ${index + 1}`,
      enabled
    };

    // Só adicionar minValue, maxValue e unit se o sensor estiver habilitado
    if (enabled) {
      if (minValue !== null) sensor.minValue = minValue;
      if (maxValue !== null) sensor.maxValue = maxValue;
      if (unit) sensor.unit = unit;
    }

    return sensor;
  });

  return {
    equipmentSerial,
    equipmentName,
    isActive: true,
    notificationMethods: {
      email: notification.channel?.email || false,
      app: notification.channel?.app || false
    },
    recipients: {
      emails: notification.channel?.notifyTo || []
    },
    individualSensors
  };
};

const getEquipmentChannelOptions = () => {
  return [{ label: 'App', key: 'app' }, { label: 'E-mail', key: 'email' }];
};

const NotificationForm = ({ user, unit }) => {
  const [equipments, setEquipments] = useState([]);
  const [editEquip, setEditEquip] = useState({});
  const [saving, setSaving] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [successId, setSuccessId] = useState(null);
  const [copiedSettings, setCopiedSettings] = useState(null);
  const [globalConfig, setGlobalConfig] = useState(defaultPRAREquipment.notification);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isApplyingToAll, setIsApplyingToAll] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetEquipId, setResetEquipId] = useState(null);

  const [modalShow, setModalShow] = useState(false);
  const [type, setType] = useState({ color: "", status: "" });

  // Função auxiliar para obter companyId
  const getCompanyIdForEquipment = (serialNumber) => {
    const units = JSON.parse(localStorage.getItem('units')) || [JSON.parse(localStorage.getItem('cooperative'))] || [];
    const empresaEncontrada = units.find(unit => Array.isArray(unit.equipments) && unit.equipments.includes(serialNumber));
    return empresaEncontrada?._id || "";
  };

  // Função para renderizar alertas específicos do PRAR
  const renderPRARAlerts = (equip) => {
    return [
      ['changeHumidity', 'Umidade'],
      ['changeTemperature', 'Temperatura'],
      ['changePH', 'PH'],
      ['changeDensity', 'Densidade'],
      ['changeCurve', 'Curva'],
      ['status', 'Status']
    ].map(([key, label]) => (
      <Col xs={6} md={6} lg={4} key={`equip-${equip._id}-alert-${key}`}>
        <Form.Check
          type="switch"
          id={`equip-${equip._id}-alert-switch-${key}`}
          label={label}
          checked={
            editEquip[equip._id]?.notification?.[key] !== undefined
              ? editEquip[equip._id].notification[key]
              : (equip.notification?.[key] || false)
          }
          onChange={e => handleEditChange(equip._id, key, e.target.checked)}
        />
      </Col>
    ));
  };

  // Função para renderizar alertas específicos do SBAG
  const renderSBAGAlerts = (equip) => {
    return [
      ['changeUR', 'UR (Umidade Relativa)'],
      ['changeTA', 'TA (Temperatura Ambiente)'],
      ['changeTC', 'TC (Temperatura CO2)'],
      ['changeTG', 'TG (Temperatura Guia)'],
      ['changeCO2', 'CO2'],
      ['changeBattery', 'Bateria'],
      ['changeSignal', 'Sinal']
    ].map(([key, label]) => (
      <Col xs={6} md={6} lg={4} key={`equip-${equip._id}-alert-${key}`}>
        <Form.Check
          type="switch"
          id={`equip-${equip._id}-alert-switch-${key}`}
          label={label}
          checked={
            editEquip[equip._id]?.notification?.[key] !== undefined
              ? editEquip[equip._id].notification[key]
              : (equip.notification?.[key] || false)
          }
          onChange={e => handleEditChange(equip._id, key, e.target.checked)}
        />
      </Col>
    ));
  };

  // Função para renderizar limites específicos do PRAR
  const renderPRARLimits = (equip) => {
    return [
      ['Humidity', 'Umidade (%)'],
      ['Temperature', 'Temp (°C)'],
      ['PH', 'PH (pH)'],
      ['Density', 'Dens (kg/m³)']
    ].map(([key, label]) => {
      const minField = `min${key}`;
      const maxField = `max${key}`;

      const minValue = editEquip[equip._id]?.notification?.[minField] ||
        equip.notification?.[minField] || '';
      const maxValue = editEquip[equip._id]?.notification?.[maxField] ||
        equip.notification?.[maxField] || '';

      const isInvalid = !validateMinMax(minValue, maxValue, key);

      return (
        <Col xs={12} sm={6} key={`equip-${equip._id}-limit-${key}`}>
          <div className="d-flex align-items-center gap-2">
            <small className="text-nowrap" style={{ width: '90px' }}>{label}</small>
            <Form.Control
              size="sm"
              type="number"
              placeholder="Mín"
              className="text-center w-auto"
              style={{
                minWidth: '65px',
                borderColor: isInvalid ? '#dc3545' : ''
              }}
              title={`Valor mínimo para ${label}`}
              value={minValue}
              onChange={e => handleEditChange(equip._id, minField, e.target.value)}
            />
            <span className="text-muted">-</span>
            <Form.Control
              size="sm"
              type="number"
              placeholder="Máx"
              className="text-center w-auto"
              style={{
                minWidth: '65px',
                borderColor: isInvalid ? '#dc3545' : ''
              }}
              title={`Valor máximo para ${label}`}
              value={maxValue}
              onChange={e => handleEditChange(equip._id, maxField, e.target.value)}
            />
          </div>
        </Col>
      );
    });
  };

  // Função para renderizar limites específicos do SBAG
  const renderSBAGLimits = (equip) => {
    return [
      ['UR', 'UR (%)'],
      ['TA', 'TA (°C)'],
      ['TC', 'TC (°C)'],
      ['TG', 'TG (°C)'],
      ['CO2', 'CO2 (ppm)'],
      ['Battery', 'Bateria (%)'],
      ['Signal', 'Sinal (%)']
    ].map(([key, label]) => {
      const minField = `min${key}`;
      const maxField = `max${key}`;

      const minValue = editEquip[equip._id]?.notification?.[minField] ||
        equip.notification?.[minField] || '';
      const maxValue = editEquip[equip._id]?.notification?.[maxField] ||
        equip.notification?.[maxField] || '';

      const isInvalid = !validateMinMax(minValue, maxValue, key);

      return (
        <Col xs={12} sm={6} key={`equip-${equip._id}-limit-${key}`}>
          <div className="d-flex align-items-center gap-2">
            <small className="text-nowrap" style={{ width: '90px' }}>{label}</small>
            <Form.Control
              size="sm"
              type="number"
              placeholder="Mín"
              className="text-center w-auto"
              style={{
                minWidth: '65px',
                borderColor: isInvalid ? '#dc3545' : ''
              }}
              title={`Valor mínimo para ${label}`}
              value={minValue}
              onChange={e => handleEditChange(equip._id, minField, e.target.value)}
            />
            <span className="text-muted">-</span>
            <Form.Control
              size="sm"
              type="number"
              placeholder="Máx"
              className="text-center w-auto"
              style={{
                minWidth: '65px',
                borderColor: isInvalid ? '#dc3545' : ''
              }}
              title={`Valor máximo para ${label}`}
              value={maxValue}
              onChange={e => handleEditChange(equip._id, maxField, e.target.value)}
            />
          </div>
        </Col>
      );
    });
  };

  // Função para renderizar seleção de sensores individuais para SBAG
  const renderSensorSelection = (equip) => {
    const selectedSensors = editEquip[equip._id]?.notification?.selectedSensors ||
      equip.notification?.selectedSensors || [];

    const handleSelectAllSensors = () => {
      const allSensors = Array.from({ length: 10 }, (_, i) => `sensor${i + 1}`);
      handleEditChange(equip._id, 'selectedSensors', allSensors);
    };

    const handleDeselectAllSensors = () => {
      handleEditChange(equip._id, 'selectedSensors', []);
    };

    return (
      <div className="mb-3">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <Form.Label className="small fw-bold mb-0" style={{ color: verdeEscuro }}>
            Sensores Individuais para Monitoramento
          </Form.Label>
          <div className="d-flex gap-2">
            <DashMedidasButton
              variant="outline-secondary"
              onClick={handleSelectAllSensors}
              disabled={selectedSensors.length === 10}
            >
              Selecionar Todos
            </DashMedidasButton>
            <DashMedidasButton
              variant="outline-secondary"
              onClick={handleDeselectAllSensors}
              disabled={selectedSensors.length === 0}
            >
              Limpar Seleção
            </DashMedidasButton>
          </div>
        </div>

        <Row className="mb-2">
          {Array.from({ length: 10 }, (_, i) => {
            const sensorKey = `sensor${i + 1}`;
            const isSelected = selectedSensors.includes(sensorKey);

            return (
              <Col xs={6} md={4} lg={3} key={`sensor-${sensorKey}`}>
                <Form.Check
                  type="checkbox"
                  id={`equip-${equip._id}-sensor-${sensorKey}`}
                  label={`Sensor ${i + 1}`}
                  checked={isSelected}
                  onChange={e => {
                    const currentSensors = [...selectedSensors];
                    if (e.target.checked) {
                      if (!currentSensors.includes(sensorKey)) {
                        currentSensors.push(sensorKey);
                      }
                    } else {
                      const index = currentSensors.indexOf(sensorKey);
                      if (index > -1) {
                        currentSensors.splice(index, 1);
                      }
                    }
                    handleEditChange(equip._id, 'selectedSensors', currentSensors);
                  }}
                  className="sensor-checkbox"
                />
              </Col>
            );
          })}
        </Row>

        <div className="sensor-selection-info">
          <small className={selectedSensors.length === 0 ? "text-warning" : "text-muted"}>
            {selectedSensors.length === 0
              ? "⚠️ Selecione pelo menos um sensor para receber notificações"
              : `${selectedSensors.length} sensor(es) selecionado(s): ${selectedSensors.sort().join(', ')}`
            }
          </small>
        </div>
      </div>
    );
  };

  const validateMinMax = (min, max, field) => {
    const minVal = parseFloat(min);
    const maxVal = parseFloat(max);

    if (isNaN(minVal) || isNaN(maxVal)) return true;

    // Validações específicas por campo
    switch (field) {
      case 'Humidity':
        return minVal >= 0 && maxVal <= 100 && minVal <= maxVal;
      case 'Temperature':
        return minVal >= -50 && maxVal <= 100 && minVal <= maxVal;
      case 'PH':
        return minVal >= 0 && maxVal <= 14 && minVal <= maxVal;
      case 'Density':
        return minVal >= 0 && maxVal <= 2000 && minVal <= maxVal;
      case 'CO2':
        return minVal >= 0 && maxVal <= 10000 && minVal <= maxVal;
      case 'Battery':
        return minVal >= 0 && maxVal <= 100 && minVal <= maxVal;
      case 'Signal':
        return minVal >= 0 && maxVal <= 100 && minVal <= maxVal;
      default:
        return minVal <= maxVal;
    }
  };

  const handleError = (msg) => {
    setType({ color: "red", status: msg })
    setModalShow(true);
  }

  const handleSuccess = (msg) => {
    setType({ color: "#198754", status: msg })
    setModalShow(true);
  }

  useEffect(() => {
    const handleEquipamentFilter = async (equipmentsIds) => {
      try {
        const { data } = await ApiGServer.post('/equipment/filter', { filter: equipmentsIds });
        const equipments = data.map(equip => {
          const { company, ...rest } = equip;
          return {
            ...structuredClone(getDefaultEquipment(getEquipmentType(equip.serialnumber))),
            ...rest
          };
        });
        setEquipments(equipments);

      } catch (error) {
        console.error("Erro ao carregar equipamentos:", error);
      }
    }
    handleEquipamentFilter(unit.equipments);
  }, [unit]);

  const handleEditChange = (equipId, field, value) => {
    setEditEquip(prev => {
      const currentEquip = equipments.find(e => e._id === equipId);
      const equipmentType = getEquipmentType(currentEquip?.serialnumber);
      const defaultConfig = getDefaultEquipment(equipmentType);

      const existingNotification =
        prev[equipId]?.notification ||
        currentEquip?.notification ||
        defaultConfig.notification;

      if (field.startsWith('min')) {
        const maxField = field.replace('min', 'max');
        const maxValue = existingNotification[maxField];

        if (maxValue !== undefined && parseFloat(value) > parseFloat(maxValue)) {
          return {
            ...prev,
            [equipId]: {
              ...prev[equipId],
              notification: {
                ...existingNotification,
                [field]: value,
                [maxField]: value
              }
            }
          };
        }
      }

      if (field.startsWith('max')) {
        const minField = field.replace('max', 'min');
        const minValue = existingNotification[minField];

        if (minValue !== undefined && parseFloat(value) < parseFloat(minValue)) {
          return {
            ...prev,
            [equipId]: {
              ...prev[equipId],
              notification: {
                ...existingNotification,
                [field]: value,
                [minField]: value
              }
            }
          };
        }
      }

      if (field === "channel") {
        return {
          ...prev,
          [equipId]: {
            ...prev[equipId],
            notification: {
              ...existingNotification,
              channel: {
                ...existingNotification.channel,
                ...value
              }
            }
          }
        };
      }

      // Tratamento especial para selectedSensors (array)
      if (field === "selectedSensors") {
        return {
          ...prev,
          [equipId]: {
            ...prev[equipId],
            notification: {
              ...existingNotification,
              selectedSensors: value
            }
          }
        };
      }

      return {
        ...prev,
        [equipId]: {
          ...prev[equipId],
          notification: {
            ...existingNotification,
            [field]: value
          }
        }
      };
    });
  };

  const handleSave = async (equip) => {
    setSaving(true);
    setSavingId(equip._id);
    setSuccessId(null);

    try {
      const currentNotification = equip.notification || {};
      const editNotification = editEquip[equip._id]?.notification || {};

      const equipmentType = getEquipmentType(equip.serialnumber);
      const defaultConfig = getDefaultEquipment(equipmentType);

      // Validação específica para equipamentos SBAG
      if (equipmentType === 'SBAG') {
        const selectedSensors = editNotification.selectedSensors || currentNotification.selectedSensors || [];
        const hasAnyAlert = editNotification.changeUR || editNotification.changeTA ||
          editNotification.changeTC || editNotification.changeTG ||
          editNotification.changeCO2 || editNotification.changeBattery ||
          editNotification.changeSignal || currentNotification.changeUR ||
          currentNotification.changeTA || currentNotification.changeTC ||
          currentNotification.changeTG || currentNotification.changeCO2 ||
          currentNotification.changeBattery || currentNotification.changeSignal;

        if (hasAnyAlert && selectedSensors.length === 0) {
          setType({
            color: "warning",
            status: "Para equipamentos SBAG com alertas ativos, é necessário selecionar pelo menos um sensor para monitoramento."
          });
          setModalShow(true);
          setSaving(false);
          setSavingId(null);
          return;
        }
      }

      const mergedChannel = {
        ...defaultConfig.notification.channel,
        ...currentNotification.channel,
        ...editNotification.channel,
      };

      mergedChannel.notifyTo = editNotification.channel?.notifyTo ||
        currentNotification.channel?.notifyTo ||
        defaultConfig.notification.channel.notifyTo;

      const updatedNotification = {
        ...defaultConfig.notification,
        ...currentNotification,
        ...editNotification,
        channel: mergedChannel
      };

      // Para equipamentos SBAG, usar os novos endpoints
      if (equipmentType === 'SBAG') {
        const companyId = getCompanyIdForEquipment(equip.serialnumber);

        if (!companyId) {
          throw new Error('Company ID não encontrado para este equipamento');
        }

        const selectedSensors = updatedNotification.selectedSensors || [];

        // Decidir qual formato usar baseado na presença de sensores selecionados
        let dataToSend;
        if (selectedSensors.length > 0) {
          // Usar formato de sensores individuais
          dataToSend = transformToIndividualSensorsFormat(
            updatedNotification,
            equip.serialnumber,
            equip.nameEquipament,
            selectedSensors
          );
        } else {
          // Usar formato de tipos de alerta
          dataToSend = transformToAlertTypesFormat(
            updatedNotification,
            equip.serialnumber,
            equip.nameEquipament
          );
        }

        await ApiMServer.post(`/api/v2/notifications/config/${companyId}`, dataToSend);
      } else {
        // Para equipamentos PRAR, manter o endpoint atual
        const equipmentToSend = {
          ...equip,
          notification: updatedNotification
        };

        await ApiGServer.put(`/equipment/update-notification`, [equipmentToSend]);
      }

      setEquipments(prev => prev.map(eq => {
        if (eq._id === equip._id) {
          return {
            ...eq,
            notification: updatedNotification
          };
        }
        return eq;
      }));

      setEditEquip(prev => ({ ...prev, [equip._id]: undefined }));
      setSuccessId(equip._id);
      setTimeout(() => setSuccessId(null), 2000);
    } catch (e) {
      handleError("Erro ao salvar configuração!");
      console.error("Erro ao salvar equipamento:", e);
    }
    setSaving(false);
    setSavingId(null);
  };

  const copySettings = (equip) => {
    const equipmentType = getEquipmentType(equip.serialnumber);
    const defaultConfig = getDefaultEquipment(equipmentType);

    const notificationToStore = {
      ...defaultConfig.notification,
      ...equip.notification
    };

    if (equip.notification?.channel) {
      notificationToStore.channel = {
        ...defaultConfig.notification.channel,
        ...equip.notification.channel
      };
    }

    // Incluir selectedSensors para equipamentos SBAG
    if (equipmentType === 'SBAG' && equip.notification?.selectedSensors) {
      notificationToStore.selectedSensors = [...equip.notification.selectedSensors];
    }

    setCopiedSettings({
      notification: notificationToStore,
      equipmentType: equipmentType // Armazenar o tipo para validação na colagem
    });
  };

  const resetSettings = (equipId) => {
    setResetEquipId(equipId);
    setShowResetModal(true);
  };

  const handleConfirmReset = async () => {
    if (!resetEquipId) return;

    setShowResetModal(false);
    setSaving(true);
    setSavingId(resetEquipId);

    try {
      const equipToReset = equipments.find(eq => eq._id === resetEquipId);

      if (!equipToReset) {
        throw new Error("Equipamento não encontrado");
      }

      const equipmentType = getEquipmentType(equipToReset.serialnumber);
      const defaultConfig = getDefaultEquipment(equipmentType);

      const resetEquipment = {
        ...equipToReset,
        notification: { ...defaultConfig.notification }
      };

      if (equipmentType === 'SBAG') {
        // Para equipamentos SBAG, usar novos endpoints
        const companyId = getCompanyIdForEquipment(equipToReset.serialnumber);

        if (!companyId) {
          throw new Error("Company ID não encontrado para o equipamento SBAG");
        }

        // Usar transformação para alert types (configuração padrão)
        const dataToSend = transformToAlertTypesFormat(
          resetEquipment.notification,
          equipToReset.serialnumber,
          equipToReset.nameEquipament
        );

        console.log("Enviando configurações resetadas SBAG:", dataToSend);
        await ApiMServer.post(`/api/v2/notifications/config/${companyId}`, dataToSend);
      } else {
        // Para equipamentos PRAR, manter endpoint atual
        console.log("Enviando configurações resetadas PRAR:", [resetEquipment]);
        await ApiGServer.put(`/equipment/update-notification`, [resetEquipment]);
      }

      setEquipments(prev => prev.map(eq => {
        if (eq._id === resetEquipId) {
          return resetEquipment;
        }
        return eq;
      }));

      setEditEquip(prev => ({ ...prev, [resetEquipId]: undefined }));

      setSuccessId(resetEquipId);
      setTimeout(() => setSuccessId(null), 2000);
      handleSuccess("Configurações resetadas com sucesso!");
    } catch (e) {
      handleError("Erro ao resetar configurações!");
      console.error("Erro ao resetar configurações:", e);
    } finally {
      setSaving(false);
      setSavingId(null);
      setResetEquipId(null);
    }
  };

  const pasteSettings = (equipId) => {
    if (!copiedSettings) return;

    const targetEquip = equipments.find(e => e._id === equipId);
    const targetEquipmentType = getEquipmentType(targetEquip?.serialnumber);

    // Verificar compatibilidade de tipos
    if (copiedSettings.equipmentType && copiedSettings.equipmentType !== targetEquipmentType) {
      setType({ color: "warning", status: "Configurações copiadas são de um tipo de equipamento diferente. Apenas campos compatíveis serão aplicados." });
      setModalShow(true);
    }

    setEditEquip(prev => {
      const existingConfig = prev[equipId] || {};
      const defaultConfig = getDefaultEquipment(targetEquipmentType);

      // Filtrar campos compatíveis baseado no tipo de equipamento
      let filteredNotification = { ...copiedSettings.notification };

      if (targetEquipmentType === 'PRAR') {
        // Para PRAR, remover campos específicos do SBAG
        delete filteredNotification.changeUR;
        delete filteredNotification.changeTA;
        delete filteredNotification.changeTC;
        delete filteredNotification.changeTG;
        delete filteredNotification.changeCO2;
        delete filteredNotification.changeBattery;
        delete filteredNotification.changeSignal;
        delete filteredNotification.minUR;
        delete filteredNotification.maxUR;
        delete filteredNotification.minTA;
        delete filteredNotification.maxTA;
        delete filteredNotification.minTC;
        delete filteredNotification.maxTC;
        delete filteredNotification.minTG;
        delete filteredNotification.maxTG;
        delete filteredNotification.minCO2;
        delete filteredNotification.maxCO2;
        delete filteredNotification.minBattery;
        delete filteredNotification.maxBattery;
        delete filteredNotification.minSignal;
        delete filteredNotification.maxSignal;
        delete filteredNotification.selectedSensors;
      } else if (targetEquipmentType === 'SBAG') {
        // Para SBAG, remover campos específicos do PRAR
        delete filteredNotification.changePH;
        delete filteredNotification.changeDensity;
        delete filteredNotification.changeCurve;
        delete filteredNotification.minPH;
        delete filteredNotification.maxPH;
        delete filteredNotification.minDensity;
        delete filteredNotification.maxDensity;
      }

      return {
        ...prev,
        [equipId]: {
          ...existingConfig,
          notification: {
            ...defaultConfig.notification,
            ...(existingConfig.notification || {}),
            ...filteredNotification,
            channel: {
              ...defaultConfig.notification.channel,
              ...(existingConfig.notification?.channel || {}),
              ...copiedSettings.notification.channel
            }
          }
        }
      };
    });
  };

  const handleApplyToAll = () => {
    setShowConfirmModal(true);
  };

  const handleConfirmApplyToAll = async () => {
    setShowConfirmModal(false);
    setIsApplyingToAll(true);

    try {
      const completeEquipments = equipments.map(equip => {
        const equipmentType = getEquipmentType(equip.serialnumber);
        const defaultConfig = getDefaultEquipment(equipmentType);
        const currentNotification = equip.notification || defaultConfig.notification;

        // Filtrar campos compatíveis baseado no tipo de equipamento
        let filteredGlobalConfig = { ...globalConfig };

        if (equipmentType === 'PRAR') {
          // Para PRAR, remover campos específicos do SBAG
          delete filteredGlobalConfig.changeUR;
          delete filteredGlobalConfig.changeTA;
          delete filteredGlobalConfig.changeTC;
          delete filteredGlobalConfig.changeTG;
          delete filteredGlobalConfig.changeCO2;
          delete filteredGlobalConfig.changeBattery;
          delete filteredGlobalConfig.changeSignal;
          delete filteredGlobalConfig.minUR;
          delete filteredGlobalConfig.maxUR;
          delete filteredGlobalConfig.minTA;
          delete filteredGlobalConfig.maxTA;
          delete filteredGlobalConfig.minTC;
          delete filteredGlobalConfig.maxTC;
          delete filteredGlobalConfig.minTG;
          delete filteredGlobalConfig.maxTG;
          delete filteredGlobalConfig.minCO2;
          delete filteredGlobalConfig.maxCO2;
          delete filteredGlobalConfig.minBattery;
          delete filteredGlobalConfig.maxBattery;
          delete filteredGlobalConfig.minSignal;
          delete filteredGlobalConfig.maxSignal;
          delete filteredGlobalConfig.selectedSensors;
        } else if (equipmentType === 'SBAG') {
          // Para SBAG, remover campos específicos do PRAR
          delete filteredGlobalConfig.changePH;
          delete filteredGlobalConfig.changeDensity;
          delete filteredGlobalConfig.changeCurve;
          delete filteredGlobalConfig.minPH;
          delete filteredGlobalConfig.maxPH;
          delete filteredGlobalConfig.minDensity;
          delete filteredGlobalConfig.maxDensity;
        }

        const updatedNotification = {
          ...defaultConfig.notification,
          ...currentNotification,
          ...filteredGlobalConfig
        };

        updatedNotification.channel = {
          ...defaultConfig.notification.channel,
          ...currentNotification.channel,
          ...globalConfig.channel
        };

        return {
          ...equip,
          notification: updatedNotification
        };
      });

      setEquipments(completeEquipments);

      setEditEquip({});

      // Separar equipamentos por tipo para usar endpoints corretos
      const prarEquipments = completeEquipments.filter(equip => getEquipmentType(equip.serialnumber) === 'PRAR');
      const sbagEquipments = completeEquipments.filter(equip => getEquipmentType(equip.serialnumber) === 'SBAG');

      // Processar equipamentos PRAR com endpoint atual
      if (prarEquipments.length > 0) {
        console.log("Enviando equipamentos PRAR:", prarEquipments);
        await ApiGServer.put('/equipment/update-notification', prarEquipments);
      }

      // Processar equipamentos SBAG com novos endpoints
      if (sbagEquipments.length > 0) {
        console.log("Enviando equipamentos SBAG:", sbagEquipments);

        for (const equip of sbagEquipments) {
          const companyId = getCompanyIdForEquipment(equip.serialnumber);

          if (!companyId) {
            console.warn(`Company ID não encontrado para equipamento SBAG: ${equip.serialnumber}`);
            continue;
          }

          // Determinar se usar individual sensors ou alert types
          let dataToSend;
          if (equip.notification.selectedSensors && equip.notification.selectedSensors.length > 0) {
            dataToSend = transformToIndividualSensorsFormat(
              equip.notification,
              equip.serialnumber,
              equip.nameEquipament,
              equip.notification.selectedSensors
            );
          } else {
            dataToSend = transformToAlertTypesFormat(
              equip.notification,
              equip.serialnumber,
              equip.nameEquipament
            );
          }

          await ApiMServer.post(`/api/v2/notifications/config/${companyId}`, dataToSend);
        }
      }

      handleSuccess("Configurações globais aplicadas e salvas para todos os equipamentos!");
    } catch (e) {
      handleError("Erro ao aplicar configurações globais para um ou mais equipamentos!");
      console.error("Erro ao aplicar para todos em lote:", e);
    } finally {
      setIsApplyingToAll(false);
    }
  };

  return (
    <div className="settings-container dash-medidas-stack notification-form-stack">

      <NotifyModal
        type={type}
        show={modalShow}
        onHide={() => setModalShow(false)}
      />

      <DashMedidasSectionCard
        icon={FaBuilding}
        subtitle="Unidade"
        title={user?.hideSensitiveInfo ? "*****" : unit?.name?.toUpperCase() || "Empresa sem nome"}
        headerRight={
          <span className="dash-section-card__header-meta">
            {unit?.equipments?.length || 0} equipamentos
          </span>
        }
        collapsible
        sectionId={`notification-geral-${unit?._id || "unit"}`}
      >
        <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
          <h6 className="fw-bold mb-0 notification-form-label">Notificações Gerais</h6>
          <DashMedidasButton
            variant="outline-success"
            onClick={handleApplyToAll}
            disabled={isApplyingToAll}
          >
            {isApplyingToAll ? (
              <>
                <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                Aplicando...
              </>
            ) : (
              "Aplicar para todos"
            )}
          </DashMedidasButton>
        </div>

        <Row className="g-3">
          <Col md={12} lg={4}>
            <Form.Group className="mb-2">
              <Form.Label className="small fw-bold mb-1 notification-form-label">Canais de Notificação</Form.Label>
              <div className="d-flex flex-column gap-1">
                {[{ label: 'App', key: 'app' }, { label: 'E-mail', key: 'email' }].map(channel => (
                  <Form.Check
                    key={channel.label}
                    type="switch"
                    id={`global-channel-${channel.key}`}
                    label={channel.label}
                    checked={globalConfig.channel?.[channel.key] ?? false}
                    onChange={e => setGlobalConfig(prev => ({
                      ...prev,
                      channel: {
                        ...prev.channel,
                        [channel.key]: e.target.checked
                      }
                    }))}
                  />
                ))}
              </div>
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label className="small fw-bold mb-1 notification-form-label">Lista de E-mails (Geral)</Form.Label>
              <ReactMultiEmail
                placeholder="email1@exemplo.com, email2@exemplo.com"
                emails={globalConfig.channel?.notifyTo || []}
                onChange={(emails) => {
                  setGlobalConfig(prev => ({
                    ...prev,
                    channel: {
                      ...prev.channel,
                      notifyTo: emails
                    }
                  }));
                }}
                validateEmail={email => isEmail(email)}
                getLabel={(email, index, removeEmail) => (
                  <div data-tag key={index}>
                    {email}
                    <span data-tag-handle onClick={() => removeEmail(index)}>
                      ×
                    </span>
                  </div>
                )}
              />
            </Form.Group>
          </Col>

          <Col md={12} lg={8}>
            <Form.Group className="mb-2">
              <Form.Label className="small fw-bold mb-1 notification-form-label">Alertas Gerais</Form.Label>
              <Row className="g-2">
                {[
                  ['changeHumidity', 'Umidade'],
                  ['changeTemperature', 'Temperatura'],
                  ['changePH', 'PH'],
                  ['changeDensity', 'Densidade'],
                  ['changeCurve', 'Curva'],
                  ['changeUR', 'UR (Umidade Relativa)'],
                  ['changeTA', 'TA (Temperatura Ambiente)'],
                  ['changeTC', 'TC (Temperatura CO2)'],
                  ['changeTG', 'TG (Temperatura Guia)'],
                  ['changeCO2', 'CO2'],
                  ['changeBattery', 'Bateria'],
                  ['changeSignal', 'Sinal'],
                  ['status', 'Status']
                ].map(([key, label]) => (
                  <Col xs={6} md={6} lg={4} key={`global-alert-${key}`}>
                    <Form.Check
                      type="switch"
                      id={`global-alert-switch-${key}`}
                      label={label}
                      checked={globalConfig[key] ?? false}
                      onChange={e => setGlobalConfig(prev => ({
                        ...prev,
                        [key]: e.target.checked
                      }))}
                    />
                  </Col>
                ))}
              </Row>
            </Form.Group>
          </Col>

          <Col xs={12}>
            <Form.Label className="small fw-bold mb-1 mt-2 notification-form-label">Limites de Alerta (Mínimo - Máximo)</Form.Label>
            <Row className="g-3">
              {[
                ['Humidity', 'Umidade (%)'],
                ['Temperature', 'Temp (°C)'],
                ['PH', 'PH (pH)'],
                ['Density', 'Dens (kg/m³)'],
                ['UR', 'UR (%)'],
                ['TA', 'TA (°C)'],
                ['TC', 'TC (°C)'],
                ['TG', 'TG (°C)'],
                ['CO2', 'CO2 (ppm)'],
                ['Battery', 'Bateria (%)'],
                ['Signal', 'Sinal (%)']
              ].map(([key, label]) => {
                const minField = `min${key}`;
                const maxField = `max${key}`;
                const isInvalid = !validateMinMax(globalConfig[minField], globalConfig[maxField], key);

                return (
                  <Col xs={12} sm={6} md={3} key={`global-limit-${key}`}>
                    <div className="d-flex align-items-center gap-2">
                      <small className="text-nowrap" style={{ width: '90px' }}>{label}</small>
                      <Form.Control
                        size="sm"
                        type="number"
                        placeholder="Mín"
                        className={`text-center ${isInvalid ? "input-invalid" : ""}`}
                        style={{ width: '70px' }}
                        value={globalConfig[minField] || ''}
                        onChange={e => {
                          const newValue = e.target.value;
                          setGlobalConfig(prev => {
                            if (prev[maxField] !== undefined && parseFloat(newValue) > parseFloat(prev[maxField])) {
                              return {
                                ...prev,
                                [minField]: newValue,
                                [maxField]: newValue
                              };
                            }
                            return { ...prev, [minField]: newValue };
                          });
                        }}
                      />
                      <span className="text-muted">-</span>
                      <Form.Control
                        size="sm"
                        type="number"
                        placeholder="Máx"
                        className={`text-center ${isInvalid ? "input-invalid" : ""}`}
                        style={{ width: '70px' }}
                        value={globalConfig[maxField] || ''}
                        onChange={e => {
                          const newValue = e.target.value;
                          setGlobalConfig(prev => {
                            if (prev[minField] !== undefined && parseFloat(newValue) < parseFloat(prev[minField])) {
                              return {
                                ...prev,
                                [maxField]: newValue,
                                [minField]: newValue
                              };
                            }
                            return { ...prev, [maxField]: newValue };
                          });
                        }}
                      />
                    </div>
                  </Col>
                );
              })}
            </Row>
          </Col>
        </Row>
      </DashMedidasSectionCard>

      {equipments && equipments.length > 0 ? (
        <div className="notification-equip-grid">
          {equipments.map((equip, idx) => (
            <DashMedidasSectionCard
              key={equip._id || idx}
              icon={FaMicrochip}
              subtitle={equip?.serialnumber || "Equipamento"}
              title={equip?.nameEquipament || equip?.serialnumber || "--"}
              headerRight={
                <div className="notification-equip-actions">
                  <DashMedidasButton
                    variant="outline-danger"
                    onClick={() => resetSettings(equip._id)}
                  >
                    Resetar
                  </DashMedidasButton>
                  <DashMedidasButton
                    variant="outline-secondary"
                    onClick={() => copySettings(equip)}
                  >
                    Copiar
                  </DashMedidasButton>
                  {copiedSettings && (
                    <DashMedidasButton
                      variant="outline-success"
                      onClick={() => pasteSettings(equip._id)}
                    >
                      Colar
                    </DashMedidasButton>
                  )}
                </div>
              }
              collapsible
              sectionId={`notification-equip-${equip._id || idx}`}
            >
              <Form>
                <div className="mb-2">
                  <Form.Label className="small fw-bold mb-1 notification-form-label">Canais</Form.Label>
                  <div className="d-flex flex-wrap gap-2">
                    {[{ label: 'App', key: 'app' }, { label: 'E-mail', key: 'email' }].map(channel => (
                      <Form.Check
                        key={`equip-${equip._id}-channel-${channel.label}`}
                        type="switch"
                        id={`equip-${equip._id}-channel-switch-${channel.key}`}
                        label={channel.label}
                        checked={
                          editEquip[equip._id]?.notification?.channel?.[channel.key] !== undefined
                            ? editEquip[equip._id].notification.channel[channel.key]
                            : (equip.notification?.channel?.[channel.key] || false)
                        }
                        onChange={e => {
                          const currentChannel = editEquip[equip._id]?.notification?.channel ||
                            equip.notification?.channel || {};

                          handleEditChange(equip._id, "channel", {
                            ...currentChannel,
                            [channel.key]: e.target.checked
                          });
                        }}
                      />
                    ))}
                  </div>
                </div>

                <Form.Group className="mb-2">
                  <Form.Label className="small fw-bold mb-1 notification-form-label">Destinatários</Form.Label>
                  <ReactMultiEmail
                    placeholder="email1, email2..."
                    emails={editEquip[equip._id]?.notification?.channel?.notifyTo || equip.notification?.channel?.notifyTo || []}
                    onChange={(emails) => {
                      const equipmentType = getEquipmentType(equip.serialnumber);
                      const defaultConfig = getDefaultEquipment(equipmentType);
                      const currentNotification = editEquip[equip._id]?.notification ||
                        equip.notification || defaultConfig.notification;

                      const currentChannel = currentNotification.channel || {};

                      const updatedChannel = {
                        ...currentChannel,
                        notifyTo: emails
                      };

                      setEditEquip(prev => ({
                        ...prev,
                        [equip._id]: {
                          ...prev[equip._id],
                          notification: {
                            ...currentNotification,
                            channel: updatedChannel
                          }
                        }
                      }));
                    }}
                    validateEmail={email => isEmail(email)}
                    getLabel={(email, index, removeEmail) => (
                      <div data-tag key={index}>
                        {email}
                        <span data-tag-handle onClick={() => removeEmail(index)}>
                          ×
                        </span>
                      </div>
                    )}
                  />
                </Form.Group>

                {(() => {
                  const equipmentType = getEquipmentType(equip.serialnumber);

                  if (equipmentType === 'SBAG') {
                    return (
                      <>
                        {renderSensorSelection(equip)}

                        <div className="mb-2">
                          <Form.Label className="small fw-bold mb-1 notification-form-label">Alertas</Form.Label>
                          <Row className="g-2">
                            {renderSBAGAlerts(equip)}
                          </Row>
                        </div>

                        <div>
                          <Form.Label className="small fw-bold mb-1 notification-form-label">Limites (Mín - Máx)</Form.Label>
                          <Row className="g-2">
                            {renderSBAGLimits(equip)}
                          </Row>
                        </div>
                      </>
                    );
                  }

                  return (
                    <>
                      <div className="mb-2">
                        <Form.Label className="small fw-bold mb-1 notification-form-label">Alertas</Form.Label>
                        <Row className="g-2">
                          {renderPRARAlerts(equip)}
                        </Row>
                      </div>

                      <div>
                        <Form.Label className="small fw-bold mb-1 notification-form-label">Limites (Mín - Máx)</Form.Label>
                        <Row className="g-2">
                          {renderPRARLimits(equip)}
                        </Row>
                      </div>
                    </>
                  );
                })()}
              </Form>

              <div className="mt-3">
                <DashMedidasButton
                  variant="success"
                  className={[
                    "w-100 notification-save-btn",
                    successId === equip._id ? "is-success" : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  disabled={saving && savingId === equip._id}
                  onClick={() => handleSave(equip)}
                >
                  {saving && savingId === equip._id ? (
                    <><Spinner size="sm" className="me-2" /> Salvando...</>
                  ) : successId === equip._id ? (
                    <>✓ Salvo</>
                  ) : (
                    <>Salvar</>
                  )}
                </DashMedidasButton>
              </div>
            </DashMedidasSectionCard>
          ))}
        </div>
      ) : (
        <Alert variant="warning" className="mb-0 notification-empty-alert text-center">
          <FaMicrochip size={28} className="mb-2 d-block mx-auto" style={{ opacity: 0.55 }} />
          Nenhum equipamento encontrado
        </Alert>
      )}

      <Modal show={showResetModal} onHide={() => setShowResetModal(false)} centered>
        <Modal.Header closeButton className="modal-border">
          <Modal.Title className="notification-form-label">Confirmar Reset</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Tem certeza de que deseja resetar todas as configurações de notificação deste equipamento para os valores padrões?
        </Modal.Body>
        <Modal.Footer className="modal-border">
          <DashMedidasButton
            variant="outline-secondary"
            onClick={() => setShowResetModal(false)}
          >
            Cancelar
          </DashMedidasButton>
          <DashMedidasButton
            variant="danger"
            onClick={handleConfirmReset}
          >
            Resetar Configurações
          </DashMedidasButton>
        </Modal.Footer>
      </Modal>

      <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} centered>
        <Modal.Header closeButton className="modal-border">
          <Modal.Title className="notification-form-label">Confirmar Aplicação Geral</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Tem certeza de que deseja aplicar as configurações gerais a todos os equipamentos listados? As configurações individuais de cada equipamento serão sobrescritas.
        </Modal.Body>
        <Modal.Footer className="modal-border">
          <DashMedidasButton
            variant="outline-secondary"
            onClick={() => setShowConfirmModal(false)}
          >
            Cancelar
          </DashMedidasButton>
          <DashMedidasButton
            variant="success"
            onClick={handleConfirmApplyToAll}
          >
            Confirmar e Aplicar
          </DashMedidasButton>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default NotificationForm;
